import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import { usePageBreadcrumbs, useTabCrumb } from '@app/src/components/Navigation/Breadcrumbs/usePageBreadcrumbs'
import type { TabsProps } from '@app/src/components/Tabs'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import usePrevious from '@app/src/hooks/previous'
import { useQueryState, useStringifyQueryFilters } from '@app/src/hooks/queryState'
import { useFetchAssessmentSettings } from '@app/src/pages/Assessments/useFetchAssessmentSettings'
import type { FilterInterface } from '@app/src/pages/ResourceCollection/Filters/Filters'
import { DEFAULT_FILTER_NAME } from '@app/src/pages/ResourceCollection/Filters/Filters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import { AmplitudeTrackingEvents, ONE_MINUTE_MS } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Redirect } from 'react-router'
import { generatePath, Route, Switch, useParams } from 'react-router-dom'
import AllAssessmentsCompareScene from './AllAssessmentsCompareScene'
import AssessmentOverviewScene from './AllAssessmentsOverviewScene'

export enum AllAssessmentsViews {
  Overview = 'overview',
  Compare = 'compare',
  Details = 'details',
}

interface AllAssessmentsSceneParams {
  view: AllAssessmentsViews
}

export const allAssessmentsAllowedFilters = [
  'assessments.id',
  'assessments.periodName',
  'assessments.totalLevel',
  'assessments.percentage',
  'assessments.assessmentTemplate.name',
  'assessments.assessmentTemplate.id',
  'id',
  'name',
  'provider.country.id',
  'provider.createdAt',
  'provider.providerOwners.userId',
  'provider.providerOwners.user',
  'provider.websiteAddress',
  'provider.vatNumber',
  'provider.registrationNumber',
  'provider.linkStatus',
  'categoryOptions.id',
  'provider.activityStatus',
  'provider.finalRiskRating',
  'provider.priority',
  'provider.providerApprovalStatus',
  'provider.supplierUsage',
  'provider.tier',
  'organization.id',
  'provider.mappingNodes.actorType.id',
  'provider.mappingNodes.tier',
  'assessmentTemplate.id',
  'periodName',
  'provider.id',
  'provider.categoryOptions.id',
  'provider.spends.spendClassification',
  'provider.spends.periodName',
  'totalLevel',
  'percentage',
  'assessmentTemplate.name',
  'section1Level',
  'section2Level',
  'section3Level',
  'section4Level',
  'section5Level',
  'isScreened',
]

const AllAssessmentsScene: React.FC = () => {
  const { hasPermission } = usePermissions()
  const { formatMessage } = useIntl()
  const { trackEvent } = useAmplitude()
  const { stringifyQueryFilters } = useStringifyQueryFilters()
  const { view } = useParams<AllAssessmentsSceneParams>()
  const [filters, setFilters] = useQueryState<Array<FilterInterface>>(DEFAULT_FILTER_NAME, [])
  const previousFilters = usePrevious(filters)

  useEffect(() => {
    if (previousFilters?.length) setFilters(previousFilters)
  }, [view])

  const canAccessAssessmentTemplate = hasPermission(Permissions.ASSESSMENT_TEMPLATE_ACCESS)

  const actionTabs: TabsProps['tabs'] = [
    {
      type: AllAssessmentsViews.Overview,
      url: stringifyQueryFilters({
        url: generatePath(paths.allAssessments, { view: AllAssessmentsViews.Overview }),
        queryParams: {},
      }),
      label: formatMessage({ id: 'assessment.tabs.overview' }),
    },
    {
      type: AllAssessmentsViews.Compare,
      url: stringifyQueryFilters({
        url: generatePath(paths.allAssessments, { view: AllAssessmentsViews.Compare }),
        queryParams: {},
      }),
      label: formatMessage({ id: 'assessment.tabs.compare' }),
    },
  ]

  useEffect(() => {
    trackEvent({
      name: AmplitudeTrackingEvents.Analyze.Assessment.ViewedPage,
      eventProps: {
        page: view,
      },
    })
  }, [view])

  const assessmentsCrumb = { label: formatMessage({ id: 'navbar.assessments' }) }
  const allAssessmentsCrumb = {
    label: formatMessage({ id: 'navbar.allAssessments' }),
    to: generatePath(paths.allAssessments, { view: AllAssessmentsViews.Overview }),
  }
  const tabCrumb = useTabCrumb(actionTabs, view)
  usePageBreadcrumbs(
    tabCrumb ? [assessmentsCrumb, allAssessmentsCrumb, tabCrumb] : [assessmentsCrumb, allAssessmentsCrumb],
  )

  const { assessmentSettings } = useFetchAssessmentSettings()
  const assessmentTemplates =
    assessmentSettings
      ?.filter(assessmentSetting => assessmentSetting.isActivated)
      .map(assessmentSetting => assessmentSetting.assessmentTemplate) ?? []
  const assessmentTemplateIds = assessmentTemplates.map(assessmentTemplate => assessmentTemplate.id)

  const facetFilter = [
    {
      name: 'assessmentTemplateId',
      filters: [
        {
          operator: Operators.In,
          value: assessmentTemplateIds,
        },
      ],
    },
  ]

  const {
    facets: [assessmentResultPeriodNameFacet = []],
  } = useFetchFacets({
    key: [FetchKey.FilterFacet, 'periodName'],
    endpoint: endpoints.assessmentFacet,
    facetsParam: [{ name: 'periodName', isEnum: true }],
    filter: facetFilter,
    sort: {
      order: SortOrder.DESCENDING,
      target: 'value',
    },
    options: { enabled: Boolean(assessmentTemplateIds?.length), staleTime: ONE_MINUTE_MS },
  })
  const periodNames = assessmentResultPeriodNameFacet.map(facet => facet.value) as string[]

  if (!canAccessAssessmentTemplate) return null

  if (!view) {
    return (
      <Redirect
        to={generatePath(paths.allAssessments, {
          view: AllAssessmentsViews.Overview,
        })}
      />
    )
  }

  return (
    <>
      <Switch>
        <Route path={generatePath(paths.allAssessments, { view: AllAssessmentsViews.Overview })} exact>
          <AssessmentOverviewScene
            tabs={{ actionTabs, activeTabParam: view }}
            allowedFilters={allAssessmentsAllowedFilters}
            activatedAssessmentTemplates={assessmentTemplates}
            assessedPeriodNames={periodNames}
          />
        </Route>
        <Route path={generatePath(paths.allAssessments, { view: AllAssessmentsViews.Compare })} exact>
          <AllAssessmentsCompareScene
            tabs={{ actionTabs, activeTabParam: view }}
            allowedFilters={allAssessmentsAllowedFilters}
            activatedAssessmentTemplates={assessmentTemplates}
            assessedPeriodNames={periodNames}
          />
        </Route>
      </Switch>
    </>
  )
}

export default AllAssessmentsScene
